import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import Swal from "sweetalert2";
import {
  Breadcrumb,
  DatePicker,
  Descriptions,
  Dropdown,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tooltip,
  Typography,
  Card,
  Row,
  Col as AntCol,
  Divider,
} from "antd";
import {
  EditAccess,
  CancelAccess,
} from "../../../components/accessControl/AccessControl";
import { useAccessControl } from "../../../utils/useAccessControl";
import { Spin, Tag } from "antd/lib";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ReactTyped } from "react-typed";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import debounce from "lodash.debounce";
import FormDatePicker from "../../form-components/DatePicker";
import { tagRender } from "../projects/projectCreation";
import dayjs from "dayjs";
import { phaseApis } from "../../../api/Phase";
import { PhaseFlagEnum, PhaseTagsInterface } from "./PhasesListView";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { projectsApis } from "../../../api/projects";
import { useDispatch } from "react-redux";
import { updatePhaseInDetailModalSlice } from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { useLocation } from "react-router";
import { Icon } from "../../../components/Component";
import FormInput from "../../form-components/InputComponent";
import { getColorByLabel } from "../projects/ProjectInformationById";
const { Text } = Typography;
export type PhaseStatus =
  | "Draft"
  | "Active"
  | "InProgress"
  | "OnHold"
  | "Cancelled";
interface PhaseFieldDetailsUpdateProps {
  id: string | number;
  projectId?: string | number;
  phaseName?: string;
  phaseStartDate: any;
  phaseEndDate: any;
  phaseOwnerId?: number | string;
  phaseOwnerName?: string;
  flag: PhaseFlagEnum;
  status: PhaseStatus;
  phaseWeightage?: number;
}

export const getLabel = (list: any[], key: string) =>
  list.find((item) => item.key === key)?.label || key;

interface PhaseFieldDetailsUpdateComponentProps {
  initialMode?: "view" | "edit";
  embedded?: boolean; // When true, hides internal view/edit toggle
  onModeChange?: (mode: "view" | "edit") => void;
  onClose?: () => void;
  onRefresh?: () => void;
}

const PhaseFieldDetailsUpdate: React.FC<PhaseFieldDetailsUpdateComponentProps> = ({
  initialMode = "view",
  embedded = false,
  onModeChange,
  onClose,
  onRefresh,
}) => {
  const [statusFilterData, setStatusFilterData] = useState<SelectOption[]>([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const { id: projectId, extra: extraData } = location.state || {};
  const { screenName } = extraData || {};

  // Get access control permissions
  const { canCancel, canEdit: hasModuleEditPermission } = useAccessControl();

  const {
    data: {
      id: phaseId,
      open: phaseOpen,
      projectResponse: {
        isLocked: lock,
        lockedByName: lockedByUserName,
        updatedDate: lockedOn,
        plannedStartDate: projectStartDate,
        plannedEndDate: projectEndDate,
        status: projectStatus,
        lockedBy: lockedBy,
      },
      allowEdit,
      extra: {
        status,
        phaseOnholdDate,
        phaseETAResolution,
        phaseOnholdReason,
        ownerId,
        originalPhaseOwnerName,
        originalPhaseOwnerId,
        reassignedReason,
        phaseWeightage,
      },
    },
  } = useSelector((state: RootState) => state.PhaseInDetailModal);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{
    status: boolean;
    error: string;
    message: string;
  }>({
    status: false,
    error: "",
    message: "",
  });
  const [messageApi, contextHolder] = message.useMessage();
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [mode, setModeState] = useState<"view" | "edit">(initialMode);

  // Wrapper to handle mode changes and call callback
  const setMode = (newMode: "view" | "edit") => {
    setModeState(newMode);
    onModeChange?.(newMode);
  };
  const [originalTagIdPhaseTagIdMap, setOriginalTagIdPhaseTagIdMap] = useState<{
    [key: string]: string;
  }>({});
  const [tags, setTags] = useState<SelectOption[]>([]);
  const [originalTags, setOriginalTags] = useState<SelectOption[]>([]);
  const [deselectedTags, setDeselectedTags] = useState<any[]>([]);
  const [originalDeselectedTags, setOriginalDeselectedTags] = useState<any[]>(
    []
  );
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
  const [showStatusDropDown, setShowStatusDropDown] = useState(false);
  const [remainingWeightage, setRemainingWeightage] = useState<number | null>(
    null
  );
  const methods = useForm<PhaseFieldDetailsUpdateProps>({
    mode: "onChange",
    defaultValues: {},
  });
  const [ownerChangeModalOpen, setOwnerChangeModalOpen] = useState<{
    open: boolean;
    extraData: any;
  }>({
    open: false,
    extraData: {},
  });
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState<{
    open: boolean;
    extraData: any;
  }>({
    open: false,
    extraData: {},
  });
  const {
    reset,
    watch,
    formState: { errors, dirtyFields },
  } = methods;
  const startDateWatch = watch("phaseStartDate");

  const loadInitialTags = () => {
    if (projectTagsList && projectTagsList.length > 0) {
      const formatted = projectTagsList?.map((tag) => ({
        label: tag?.tagName,
        value: tag?.tagId,
      }));
      setOptions(formatted);
    }
  };

  const fetchTagSuggestions = async (search: string) => {
    if (!search || search.length < 1) return;
    const simulatedAPIResponse = projectTagsList?.filter((tag) =>
      tag?.tagName?.toLowerCase()?.includes(search?.toLowerCase())
    );
    const formatted = simulatedAPIResponse?.map((tag) => ({
      label: tag?.tagName,
      value: tag?.tagId,
    }));

    if (formatted.length === 0) {
      setOptions([
        {
          label: search,
          value: "newTag__" + search,
        },
      ]);
    } else {
      setOptions(formatted);
    }
  };

  const fetchProjectUsers = async () => {
    try {
      const response: any = await projectsApis.fetchProjectUsers(projectId);
      const {
        data: usersList,
        status,
      }: {
        status: boolean;
        data: { userId: string; userName: string; projectUserId: number }[];
      } = response || {};
      if (status) {
        const formatted = usersList?.map(
          (user: { userId: string; userName: string }) => ({
            label: user?.userName,
            value: +user?.userId,
          })
        );
        setProjectUsers(formatted);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchProjectTags = async () => {
    try {
      const response: any = await projectsApis.fetchProjectTags(projectId);
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { tagName: string; tagId: number }[];
      } = response || {};
      if (status) {
        setProjectTagsList(tagsList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPhaseWeightages = async () => {
    try {
      const response: any = await phaseApis.getProjectPhaseWeightage(
        Number(projectId)
      );
      const {
        data: phasesWeightage,
        status,
      }: {
        status: boolean;
        data: { phaseId: number; phaseWeightage: number }[];
      } = response || {};
      if (status) {
        // Exclude the current phase so its existing weightage is available for re-entry
        const total = (phasesWeightage || [])
          .filter((item) => Number(item?.phaseId) !== Number(phaseId))
          .reduce((sum, item) => sum + (Number(item?.phaseWeightage) || 0), 0);
        const remaining = Math.max(0, 100 - total);
        setRemainingWeightage(remaining);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const debouncedFetch = debounce(fetchTagSuggestions, 100);

  const saveChanges = (data: any) => {
    let dirtyData = getDirtyFieldsOnly(data, dirtyFields) || {};
    if (tags || deselectedTags) {
      const newLines =
        tags
          ?.filter((t: any) => typeof t?.value === "string")
          ?.map((newTag: any) => ({
            tagName: newTag?.label,
          })) || [];

      const deletedLines =
        deselectedTags?.map((t: any) => ({
          phaseTagId: originalTagIdPhaseTagIdMap[t],
        })) || [];

      if (newLines.length > 0 || deletedLines.length > 0) {
        dirtyData.phaseTags = {
          ...(newLines.length > 0 && { newLines }),
          ...(deletedLines.length > 0 && { deletedLines }),
        };
      }
    }

    if (dirtyData.phaseStartDate) {
      dirtyData.phaseStartDate = dayjs(dirtyData.phaseStartDate)?.format(
        "YYYY-MM-DD"
      );
    }
    if (dirtyData.phaseEndDate) {
      dirtyData.phaseEndDate = dayjs(dirtyData.phaseEndDate)?.format(
        "YYYY-MM-DD"
      );
    }
    if (dirtyData === undefined || Object.keys(dirtyData).length === 0) {
      setMode("view");
      setLoading(false);
      return;
    }
    const payload: {
      phaseId: number;
      projectId?: number;
      phaseName?: string;
      phaseOwnerId?: number;
      phaseStartDate?: string;
      phaseEndDate?: string;
      flag?: PhaseFlagEnum;
      phaseTags?: {
        newLines: {
          tagName: string;
        }[];
        deletedLines: {
          phaseTagId: Number;
        }[];
      };
    } = {
      phaseId: Number(phaseId),
      projectId: Number(projectId),
      ...dirtyData,
    };

    if (payload?.phaseOwnerId) {
      setLoading(false);
      setOwnerChangeModalOpen({
        open: true,
        extraData: payload,
      });
    } else updatePhase(payload);
  };

  const updatePhase = async (payload: any) => {
    try {
      const response: any = await phaseApis.updatePhase(payload);
      const { status, data, error } = response;
      if (status) {
        Swal.fire({
          title: "Success",
          text: data?.message,
          icon: "success",
          confirmButtonText: "OK",
        });
        setMode("view");
        fetchData(phaseId);
        setDeselectedTags([]);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setError({ status: true, error, message: data?.message });
        setTimeout(() => {
          setError({ status: false, message: "", error: "" });
        }, 10000);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (phaseId: any, mode?: "view" | "edit") => {
    try {
      const response: any = await phaseApis.getPhaseById(phaseId, screenName);
      const {
        data,
        status: apiStatus,
      }: {
        data: {
          phaseId: number;
          phaseName: string;
          phaseOwnerId: number;
          phaseOwnerName: string; //not in api response
          projectId: number;
          phaseStartDate: string;
          phaseEndDate: string;
          flag: PhaseFlagEnum;
          status: PhaseStatus;
          activityCompletionPercentage: string;
          tasksCompletionPercentage: string;
          totalActivities: number;
          completedActivities: number;
          totalTasks: number;
          completedTasks: number;
          archivedAt: string;
          createdAt: string;
          archivedBy: number;
          createdBy: number;
          createdByUserName: string; //not in api response
          updatedBy: number;
          phaseTags: PhaseTagsInterface[];
          phaseOnholdDate: string;
          phaseOnholdReason: string;
          phaseETAResolution: string;
          ownerId?: number;
          originalPhaseOwnerName?: string;
          originalPhaseOwnerId?: number;
          reassignedReason?: string;
          phaseWeightage?: number;
          hierarchyUserIds?: number[];
        };
        status: boolean;
      } = response || {};
      if (apiStatus) {
        const {
          projectId,
          phaseName,
          phaseStartDate,
          phaseEndDate,
          phaseOwnerId,
          phaseTags,
          status,
          createdAt,
          createdByUserName,
          phaseOwnerName,
          flag,
          phaseOnholdDate,
          phaseOnholdReason,
          phaseETAResolution,
          originalPhaseOwnerName,
          originalPhaseOwnerId,
          reassignedReason,
          phaseWeightage,
        } = data;
        setMode(mode || "view");
        const tagIdPhaseTagIdMap: any = {};
        const _tagOptions = phaseTags?.map((tag: PhaseTagsInterface) => {
          tagIdPhaseTagIdMap[tag.tagId] = tag.phaseTagId;
          return {
            label: tag.tagName,
            value: tag?.tagId,
          };
        });
        setTags(_tagOptions);
        setOriginalTags(_tagOptions);
        setOriginalTagIdPhaseTagIdMap(tagIdPhaseTagIdMap);
        setDeselectedTags([]);
        setOriginalDeselectedTags([]);
        const resetValues: any = {
          projectId,
          phaseName,
          phaseOwnerId,
          phaseOwnerName,
          flag,
          status,
          phaseWeightage: phaseWeightage || null,
        };
        if (phaseStartDate) resetValues.phaseStartDate = dayjs(phaseStartDate);
        if (phaseEndDate) resetValues.phaseEndDate = dayjs(phaseEndDate);
        reset(resetValues);
        // Check if current user is admin
        const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";

        // Recalculate base conditions from response data (prefer response, fallback to Redux store)
        const responseProjectStatus = (data as any)?.projectStatus;
        const currentProjectStatus =
          responseProjectStatus || projectStatus || "";

        // Check base conditions for status change
        const statusChangeBaseConditions =
          [
            "draft",
            "active",
            "inprogress",
            "onhold",
            "cancelled",
            "archived",
            "closed",
          ].includes(status?.toLowerCase()) &&
          !["onhold", "cancelled", "archived", "closed"].includes(
            currentProjectStatus?.toLowerCase()
          ) &&
          (lock === false || lockedBy == currentUser?.id);

        // Check if user is in hierarchyUserIds - filter out duplicates and invalid values
        const isInHierarchy =
          data?.hierarchyUserIds &&
          Array.isArray(data.hierarchyUserIds) &&
          currentUser?.id &&
          data.hierarchyUserIds
            .filter((id: number) => id > 0) // Filter out 0 and negative values
            .includes(currentUser?.id);

        // Check module-level edit permission
        const hasModulePermission = hasModuleEditPermission("Phases");

        // RESTRICTIVE: Show status dropdown based on:
        // - Owner: Module permission AND owner check
        // - Admin: Module permission only (hierarchy not needed)
        // - Others: Module permission AND hierarchy (both required)
        const canOwnerChangeStatus =
          currentUser?.id === phaseOwnerId &&
          hasModulePermission &&
          statusChangeBaseConditions;
        const canAdminChangeStatus =
          isAdmin && hasModulePermission && statusChangeBaseConditions;
        const canOthersChangeStatus =
          hasModulePermission && isInHierarchy && statusChangeBaseConditions;

        if (
          canOwnerChangeStatus ||
          canAdminChangeStatus ||
          canOthersChangeStatus
        ) {
          setShowStatusDropDown(true);
        }

        // RESTRICTIVE ACCESS: Start with false, grant only to owner, hierarchy users, admin, or users with module permission
        let _allowEdit = false;

        // Check base conditions first (using already calculated currentProjectStatus from above)
        const baseConditionsMet =
          !["onhold", "cancelled", "archived", "closed"].includes(
            status?.toLowerCase()
          ) &&
          !["onhold", "cancelled", "archived", "closed"].includes(
            currentProjectStatus?.toLowerCase()
          ) &&
          (lock === false || lockedBy == currentUser?.id);

        // Grant edit access based on:
        // - Owner: Module permission AND owner check
        // - Admin: Module permission only (hierarchy not needed)
        // - Others: Module permission AND hierarchy (both required)
        if (baseConditionsMet) {
          // Owner: Module permission AND owner check
          if (currentUser?.id === phaseOwnerId && hasModulePermission) {
            _allowEdit = true;
          }
          // Admin: Module permission only
          else if (isAdmin && hasModulePermission) {
            _allowEdit = true;
          }
          // Others: Module permission AND hierarchy (both required)
          else if (
            hasModulePermission &&
            data?.hierarchyUserIds &&
            Array.isArray(data.hierarchyUserIds) &&
            currentUser?.id
          ) {
            const validHierarchyUserIds = data.hierarchyUserIds.filter(
              (id: number) => id > 0
            );
            if (validHierarchyUserIds.includes(currentUser?.id)) {
              _allowEdit = true;
            }
          }
        }
        // Separate permission for Add Activity - less restrictive (anyone with module permission can add if conditions are met)
        let _allowAdd = false;
        if (
          !["onhold", "cancelled", "archived", "closed", "deleted"].includes(
            status?.toLowerCase()
          ) &&
          !["onhold", "cancelled", "archived", "closed"].includes(
            projectStatus?.toLowerCase()
          ) &&
          (lock === false || lockedBy == currentUser?.id)
        ) {
          _allowAdd = true;
        }

        const modalData: any = {
          phaseName,
          extra: {
            status: status,
            createdAt,
            createdByUserName,
            startDate: phaseStartDate,
            endDate: phaseEndDate,
            ownerName: phaseOwnerName,
            phaseOnholdDate: phaseOnholdDate,
            phaseETAResolution: phaseETAResolution,
            phaseOnholdReason: phaseOnholdReason,
            ownerId: phaseOwnerId,
            originalPhaseOwnerName,
            originalPhaseOwnerId,
            reassignedReason,
            phaseWeightage,
          },
        };
        modalData.allowEdit = _allowEdit;
        modalData.allowAdd = _allowAdd;

        dispatch(updatePhaseInDetailModalSlice(modalData));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule(
        "PhaseStatusChange"
      );
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const handleStatusChange = ({ key }: { key: string }) => {
    if (key?.toLocaleLowerCase() == "onhold") {
      setStatusChangeModalOpen({
        open: true,
        extraData: key,
      });
    } else {
      updatePhaseStatus(key);
    }
  };

  const updatePhaseStatus = async (
    status: string,
    phaseOnholdReason?: string,
    phaseETAResolution?: string
  ) => {
    try {
      const response = await phaseApis.updatePhaseStatus(
        phaseId,
        status,
        phaseOnholdReason,
        phaseETAResolution
      );
      const { data, status: apiStatus, message } = response || {};
      if (apiStatus) {
        Swal.fire({
          title: "Success",
          text: `Phase status updated to ${status} successfully`,
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchData(phaseId, "view");
      } else {
        Swal.fire({
          title: "Error",
          text: message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const archivePhase = async (phaseId: number) => {
    try {
      const response = await phaseApis.archivePhase(phaseId);
      const { data, status: apiStatus } = response || {};
      if (apiStatus) {
        const { id, message } = data || {};
        Swal.fire({
          title: "Success",
          text: message,
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchData(phaseId);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const deletePhase = async (phaseId: number) => {
    try {
      const response = await phaseApis.deletePhase(phaseId);
      const { data, status: apiStatus } = response || {};
      if (apiStatus) {
        const { id, message } = data || {};
        Swal.fire({
          title: "Success",
          text: message,
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchData(phaseId);
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (mode !== "view") {
      fetchProjectUsers();
      fetchProjectTags();
      if (projectId && phaseId) {
        fetchPhaseWeightages();
      }
    } else {
      setRemainingWeightage(null);
    }
  }, [mode, projectId, phaseId]);

  useEffect(() => {
    if (projectTagsList && projectTagsList.length > 0 && mode !== "view") {
      loadInitialTags();
    }
  }, [projectTagsList, mode]);

  useEffect(() => {
    if (phaseId && phaseOpen) {
      fetchStatusFilterData();
      fetchData(phaseId, initialMode);
    }
  }, [phaseId, phaseOpen]);

  if (loading) {
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin
          indicator={<LoadingOutlined spin />}
          className="m-auto"
          size="large"
        />
      </Flex>
    );
  }

  return (
    <>
      <OwnerChangeReason
        open={ownerChangeModalOpen?.open}
        setOpen={(open: boolean) => {
          setOwnerChangeModalOpen({
            open: open,
            extraData: { ...ownerChangeModalOpen?.extraData },
          });
        }}
        onSubmit={(data: any) => {
          setLoading(true);
          updatePhase({
            ...ownerChangeModalOpen?.extraData,
            reassignedReason: data,
          });
        }}
      />
      <StatusChangeReason
        open={statusChangeModalOpen?.open}
        setOpen={(open: boolean) => {
          setStatusChangeModalOpen({
            open: open,
            extraData: "",
          });
        }}
        onSubmit={(data: any, phaseETAResolution: any) => {
          updatePhaseStatus(
            statusChangeModalOpen?.extraData,
            data,
            phaseETAResolution
          );
        }}
      />
      <div className="card-head d-flex align-items-center mb-1">
        <h5 className="title mt-2">Phase Information</h5>
        {mode === "edit" ? (
          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2 ms-auto">
            <li>
              <Button
                className="btn btn-primary btn-sm"
                type="button"
                color="primary"
                onClick={methods.handleSubmit((data) => {
                  setLoading(true);
                  saveChanges(data);
                })}
              >
                <Icon name={"save"} />
                <span>Save</span>
              </Button>
            </li>
            <li>
              <Button
                className="btn btn-secondary btn-sm"
                type="button"
                color="secondary"
                onClick={() => {
                  setLoading(true);
                  fetchData(phaseId);
                  setMode("view");
                  setError({ status: false, message: "", error: "" });
                }}
              >
                <span>Cancel</span>
              </Button>
            </li>
          </ul>
        ) : (
          !embedded ? (
          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2 ms-auto">
            <>
              <li>
                <Breadcrumb>
                  <Breadcrumb.Item>
                    <span style={{ fontWeight: 500 }}>CURRENT STATUS</span>{" "}
                    :&nbsp;
                    <Text
                      style={{
                        color: "#2557d6",
                        fontWeight: 500,
                      }}
                    >
                      {getLabel(statusFilterData, status)}
                    </Text>
                    {showStatusDropDown && (
                      <EditAccess moduleName="Phases">
                        <Dropdown
                          menu={{
                            items: statusFilterData.map((item) => ({
                              key: item.value,
                              label: item.label,
                            })),
                            selectedKeys: [status],
                            onClick: handleStatusChange,
                          }}
                        >
                          <a onClick={(e) => e.preventDefault()}>
                            <Space>
                              <DownOutlined style={{ color: "#2557d6" }} />
                            </Space>
                          </a>
                        </Dropdown>
                      </EditAccess>
                    )}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </li>

              {/* Show three dots menu if user has any permissions */}
              {allowEdit || canCancel("Phases") ? (
                <li>
                  <UncontrolledDropdown>
                    <DropdownToggle
                      tag="a"
                      className="dropdown-toggle btn btn-icon btn-trigger"
                    >
                      <Icon name="more-v"></Icon>
                    </DropdownToggle>
                    <DropdownMenu end>
                      <ul className="link-list-opt">
                        <EditAccess moduleName="Phases">
                          {allowEdit && (
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#tableView"
                                onClick={(ev: any) => {
                                  ev.preventDefault();
                                  setMode("edit");
                                }}
                              >
                                <span>Edit </span>
                              </DropdownItem>
                            </li>
                          )}
                        </EditAccess>

                        {!["Archived"]?.includes(status) ? (
                          // <CancelAccess moduleName="Phases">
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#detailView"
                              onClick={(ev) => {
                                ev.preventDefault();
                                Swal.fire({
                                  title: "Are you sure?",
                                  text: "You won't be able to revert this!",
                                  icon: "warning",
                                  showCancelButton: true,
                                  confirmButtonColor: "#3085d6",
                                  cancelButtonColor: "#d33",
                                  confirmButtonText: "Yes, archive it!",
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    archivePhase(phaseId as number);
                                  }
                                });
                              }}
                            >
                              <span>Archive</span>
                            </DropdownItem>
                          </li>
                        ) : (
                          // </CancelAccess>
                          <></>
                        )}

                        <CancelAccess moduleName="Phases">
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#detailView"
                              onClick={(ev) => {
                                ev.preventDefault();
                                Swal.fire({
                                  title: "Are you sure?",
                                  text: "You won't be able to revert this!",
                                  icon: "warning",
                                  showCancelButton: true,
                                  confirmButtonColor: "#3085d6",
                                  cancelButtonColor: "#d33",
                                  confirmButtonText: "Yes, delete it!",
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    deletePhase(phaseId as number);
                                  }
                                });
                              }}
                            >
                              <span>Delete</span>
                            </DropdownItem>
                          </li>
                        </CancelAccess>
                      </ul>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </li>
              ) : (
                <></>
              )}
            </>
          </ul>
          ) : null
        )}
      </div>
      <Alert
        isOpen={!!(lock && !allowEdit && status?.toLowerCase() !== "onhold")}
        color="warning"
        className="start-0 w-100 py-1 mt-2"
        style={{
          top: 0,
          textAlign: "left",
          fontSize: "14px",
          cursor: "default",
        }}
      >
        <span className="fw-bold">Locked:</span>{" "}
        <ReactTyped
          strings={[
            `This Project is currently Locked. Last Locked on ${lockedOn} by "${lockedByUserName}".`,
          ]}
          typeSpeed={5}
          showCursor={false}
        />
      </Alert>
      <Alert
        isOpen={status?.toLowerCase() === "onhold"}
        color="warning"
        className="start-0 w-100 py-1 mt-2"
        style={{
          top: 0,
          textAlign: "left",
          fontSize: "14px",
          cursor: "default",
        }}
      >
        Phase is On-Hold on{" "}
        <span className="fw-bold">
          {dayjs(phaseOnholdDate)?.format("DD-MMM-YYYY")}
        </span>{" "}
        till{" "}
        <span className="fw-bold">
          {dayjs(phaseETAResolution)?.format("DD-MMM-YYYY")}
        </span>
        .{" "}
        <ReactTyped
          strings={["Because of, " + (phaseOnholdReason || "")]}
          typeSpeed={10}
          showCursor={false}
        />
      </Alert>
      <div className={embedded && mode === "edit" ? "p-0" : "card-inner card-inner-lg p-0"}>
        {contextHolder}
        <Alert
          isOpen={error.status}
          color="danger"
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <span className="fw-bold">{error.error}:</span>{" "}
          <ReactTyped
            strings={[error.message]}
            typeSpeed={5}
            showCursor={false}
          />
        </Alert>

        {/* Enhanced Edit Form for Embedded Mode */}
        {embedded && mode === "edit" ? (
          <div style={{ padding: "0" }}>
            <FormProvider {...methods}>
              <form>
                {/* Basic Information Card */}
                <Card
                  title={
                    <span style={{ fontSize: 16, fontWeight: 600, color: "#1a1a2e" }}>
                      Basic Information
                    </span>
                  }
                  style={{ marginBottom: 16, borderRadius: 12 }}
                  headStyle={{ borderBottom: "1px solid #f0f0f0", padding: "12px 20px" }}
                  bodyStyle={{ padding: "20px" }}
                >
                  <Row gutter={[24, 20]}>
                    <AntCol span={12}>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontWeight: 500, color: "#666", fontSize: 13 }}>Phase Name</label>
                      </div>
                      <FormInput
                        name="phaseName"
                        label={undefined}
                        disabled
                        unMaskView={true}
                      />
                    </AntCol>
                    <AntCol span={12}>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontWeight: 500, color: "#666", fontSize: 13 }}>Owner <span style={{ color: "#ff4d4f" }}>*</span></label>
                      </div>
                      <FormSelect
                        name="phaseOwnerId"
                        placeholder="Select Owner"
                        options={projectUsers}
                        label={undefined}
                        required={true}
                      />
                    </AntCol>
                    <AntCol span={12}>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontWeight: 500, color: "#666", fontSize: 13 }}>Flag</label>
                      </div>
                      <FormSelect
                        name="flag"
                        label={undefined}
                        placeholder="Select Flag"
                        options={[
                          { label: "Internal", value: "Internal" },
                          { label: "External", value: "External" },
                        ]}
                      />
                    </AntCol>
                    <AntCol span={12}>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontWeight: 500, color: "#666", fontSize: 13 }}>
                          Weightage (%)
                          {remainingWeightage !== null && (
                            <span
                              style={{
                                color: "#52c41a",
                                marginLeft: "8px",
                                fontWeight: "normal",
                              }}
                            >
                              (Avl: {remainingWeightage}%)
                            </span>
                          )}
                        </label>
                      </div>
                      <FormInput
                        name="phaseWeightage"
                        label={undefined}
                        placeholder="Enter Weightage (1-100)"
                        type="text"
                        pattern={/^(?:100|[1-9][0-9]?)$/}
                        patternErrorMessage="Enter a value between 1 to 100"
                        customValidation={{
                          validate: (value: string) => {
                            if (
                              remainingWeightage === null ||
                              value === undefined ||
                              value === null ||
                              value === ""
                            )
                              return true;
                            const n = Number(value);
                            if (Number.isNaN(n)) return true;
                            return (
                              n <= remainingWeightage ||
                              `Cannot exceed available (${remainingWeightage}%)`
                            );
                          },
                        }}
                      />
                    </AntCol>
                  </Row>
                </Card>

                {/* Schedule Card */}
                <Card
                  title={
                    <span style={{ fontSize: 16, fontWeight: 600, color: "#1a1a2e" }}>
                      Schedule
                    </span>
                  }
                  style={{ marginBottom: 16, borderRadius: 12 }}
                  headStyle={{ borderBottom: "1px solid #f0f0f0", padding: "12px 20px" }}
                  bodyStyle={{ padding: "20px" }}
                >
                  <Row gutter={[24, 20]}>
                    <AntCol span={12}>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontWeight: 500, color: "#666", fontSize: 13 }}>Planned Start Date</label>
                      </div>
                      <FormDatePicker
                        name="phaseStartDate"
                        label={undefined}
                        minDate={projectStartDate ? new Date(projectStartDate) : undefined}
                      />
                    </AntCol>
                    <AntCol span={12}>
                      <div style={{ marginBottom: 4 }}>
                        <label style={{ fontWeight: 500, color: "#666", fontSize: 13 }}>Planned End Date</label>
                      </div>
                      <FormDatePicker
                        name="phaseEndDate"
                        label={undefined}
                        disabled={!startDateWatch}
                        minDate={startDateWatch}
                        maxDate={projectEndDate ? new Date(projectEndDate) : undefined}
                      />
                    </AntCol>
                  </Row>
                </Card>

                {/* Tags Card */}
                <Card
                  title={
                    <span style={{ fontSize: 16, fontWeight: 600, color: "#1a1a2e" }}>
                      Tags
                    </span>
                  }
                  style={{ marginBottom: 24, borderRadius: 12 }}
                  headStyle={{ borderBottom: "1px solid #f0f0f0", padding: "12px 20px" }}
                  bodyStyle={{ padding: "20px" }}
                >
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Type and press enter to create or select a tag"
                    labelInValue
                    onDeselect={(e: { label: string; value: string | number }) => {
                      if (typeof e?.value === "number") {
                        setDeselectedTags((pre) => [...pre, e.value]);
                      }
                    }}
                    value={tags}
                    onSearch={debouncedFetch}
                    onChange={(value) => {
                      const latest: any = value?.slice(-1)[0];
                      if (latest?.label?.length < 2) {
                        messageApi.open({
                          type: "error",
                          content: "Enter at least 2 characters",
                          duration: 5,
                        });
                        setOptions([]);
                        return;
                      }
                      setTags(value);
                    }}
                    options={options}
                    tokenSeparators={[","]}
                    tagRender={tagRender}
                    size="large"
                  />
                </Card>

              </form>
            </FormProvider>
          </div>
        ) : (
        <div className="content clearfix">
          <FormProvider {...methods}>
            <form>
              <div className="row g-3">
                <div className="col-4">
                  <FormInput
                    name="phaseName"
                    optionLabel="Phase Name"
                    label={undefined}
                    optionLabelWidth={"140px"}
                    disabled
                    unMaskView={true}
                  />
                </div>
                <div className="col-4">
                  {mode == "view" ? (
                    <FormInput
                      name="phaseOwnerName"
                      optionLabel={
                        <span>
                          Owner{" "}
                          {originalPhaseOwnerId &&
                          originalPhaseOwnerId != ownerId ? (
                            <Popconfirm
                              title={<></>}
                              icon={<></>}
                              showCancel={false}
                              trigger="hover"
                              okButtonProps={{
                                style: {
                                  display: "none",
                                },
                              }}
                              description={
                                <Descriptions
                                  title=""
                                  bordered
                                  column={1}
                                  size="small"
                                  className="custom-description-title"
                                >
                                  <Descriptions.Item label="Original Owner">
                                    {originalPhaseOwnerName || "N/A"}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="Reason">
                                    {reassignedReason || "N/A"}
                                  </Descriptions.Item>
                                </Descriptions>
                              }
                            >
                              <Icon
                                name="info"
                                style={{
                                  cursor: "pointer",
                                  fontSize: "13px",
                                }}
                              />
                            </Popconfirm>
                          ) : (
                            <></>
                          )}
                        </span>
                      }
                      label={undefined}
                      optionLabelWidth={"140px"}
                      disabled
                      unMaskView={mode === "view"}
                    />
                  ) : (
                    <FormSelect
                      optionLabel="Owner"
                      name="phaseOwnerId"
                      placeholder="Select Owner"
                      options={projectUsers}
                      optionLabelWidth={"140px"}
                      label={undefined}
                      required={true}
                    />
                  )}
                </div>
                <div className="col-4">
                  <FormSelect
                    name="flag"
                    label={undefined}
                    optionLabel="Flag"
                    optionLabelWidth={"140px"}
                    placeholder="Select Flag"
                    options={[
                      { label: "Internal", value: "Internal" },
                      { label: "External", value: "External" },
                    ]}
                    disabled={mode == "view"}
                    unMaskView={mode === "view"}
                    customStyle={
                      mode === "view"
                        ? {
                            color: "#526484",
                            backgroundColor: "transparent",
                            border: "none",
                            boxShadow: "none",
                          }
                        : {}
                    }
                  />
                </div>
              </div>
              <div className="mt-2"></div>
              <div className="row g-3">
                <div className="col-4">
                  <FormDatePicker
                    name="phaseStartDate"
                    label={undefined}
                    optionLabelWidth={"140px"}
                    optionLabel="Planned Start Date"
                    disabled={mode === "view"}
                    unMaskView={mode === "view"}
                    minDate={
                      projectStartDate ? new Date(projectStartDate) : undefined
                    }
                  />
                </div>
                <div className="col-4">
                  <FormDatePicker
                    name="phaseEndDate"
                    label={undefined}
                    optionLabelWidth={"140px"}
                    optionLabel="Planned End Date"
                    unMaskView={mode === "view"}
                    disabled={!startDateWatch || mode == "view"}
                    minDate={startDateWatch}
                    maxDate={
                      projectEndDate ? new Date(projectEndDate) : undefined
                    }
                  />
                </div>
                <div className="col-4">
                  {mode === "view" ? (
                    <div className="form-group">
                      <div
                        className="form-control-wrap"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <span
                          style={{
                            minWidth: "140px",
                            fontWeight: 600,
                            color: "#333",
                            marginRight: "16px",
                          }}
                        >
                          Weightage
                        </span>
                        <span style={{ fontWeight: 400, color: "#526484" }}>
                          {phaseWeightage !== null &&
                          phaseWeightage !== undefined
                            ? `${phaseWeightage}%`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <FormInput
                      name="phaseWeightage"
                      label={undefined}
                      optionLabelWidth={"140px"}
                      optionLabel={
                        <span>
                          Weightage
                          {remainingWeightage !== null && (
                            <span
                              style={{
                                color: "#52c41a",
                                marginLeft: "8px",
                                fontWeight: "normal",
                              }}
                            >
                              (Avl: {remainingWeightage}%)
                            </span>
                          )}
                        </span>
                      }
                      placeholder="Enter Weightage %"
                      type="text"
                      pattern={/^(?:100|[1-9][0-9]?)$/}
                      patternErrorMessage="Enter a value between 1 to 100"
                      disabled={mode !== "edit"}
                      unMaskView={mode !== "edit"}
                      customValidation={{
                        validate: (value: string) => {
                          if (
                            remainingWeightage === null ||
                            value === undefined ||
                            value === null ||
                            value === ""
                          )
                            return true;
                          const n = Number(value);
                          if (Number.isNaN(n)) return true;
                          return (
                            n <= remainingWeightage ||
                            `Cannot exceed available (${remainingWeightage}%)`
                          );
                        },
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="mt-3"></div>
              <div className="row g-3">
                <div className="col-12">
                  {mode !== "view" ? (
                    <div className="form-group">
                      <div
                        className="form-control-wrap"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <span
                          style={{
                            minWidth: "140px",
                            fontWeight: 600,
                            color: "#333",
                            marginRight: "8px",
                          }}
                        >
                          Tags
                        </span>
                        <div style={{ flex: 1 }}>
                          <Select
                            mode="multiple"
                            style={{ width: "100%" }}
                            placeholder="Type and press enter to create or select a tag"
                            labelInValue
                            onDeselect={(e: {
                              label: string;
                              value: string | number;
                            }) => {
                              if (typeof e?.value === "number") {
                                setDeselectedTags((pre) => {
                                  return [...pre, e.value];
                                });
                              }
                            }}
                            value={tags}
                            onSearch={debouncedFetch}
                            onChange={(value) => {
                              const latest: any = value?.slice(-1)[0]; // Get the last tag
                              if (latest?.label?.length < 2) {
                                // Check if the last tag length is less than 2 characters
                                messageApi.open({
                                  type: "error",
                                  content: "Enter at least 2 characters",
                                  duration: 5,
                                });
                                setOptions([]);
                                return;
                              }
                              setTags(value);
                            }}
                            options={options}
                            tokenSeparators={[","]}
                            tagRender={tagRender}
                            filterOption={false} // Use custom filtering only
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <div
                        className="form-control-wrap"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <span
                          style={{
                            minWidth: "140px",
                            fontWeight: 600,
                            color: "#333",
                            marginRight: "25px",
                          }}
                        >
                          Tags
                        </span>
                        <div
                          style={{ flex: 1, fontWeight: 400, color: "#222" }}
                        >
                          {tags && tags.length > 0 ? (
                            tags.map((tag: any, index: number) => {
                              const color = getColorByLabel(
                                (tag.label || "").toString()
                              );
                              return (
                                <span
                                  key={index}
                                  className="custom-tag-wrapper"
                                >
                                  <Tag
                                    className="custom-tag"
                                    style={{ backgroundColor: color }}
                                  >
                                    {tag.label}
                                  </Tag>
                                  <span
                                    className="custom-tag-tail"
                                    style={{ borderLeftColor: color }}
                                  />
                                </span>
                              );
                            })
                          ) : (
                            <span>N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
        )}
      </div>
    </>
  );
};

export const OwnerChangeReason = ({ open, setOpen, onSubmit }: any) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        onSubmit(reason); // Call your reject function here
        form.resetFields();
        setOpen(false);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <Modal
      title={undefined}
      open={open}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" name="ownerChange">
        <Form.Item
          name="reason"
          label="Reason for Owner Change"
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." rows={4} />
        </Form.Item>
      </Form>
      <div className="actions clearfix">
        <ul className="pt-2">
          <li>
            <Button color="primary" type="submit" onClick={handleOk}>
              Submit
            </Button>
          </li>
          <li>
            <Button
              color="primary"
              onClick={() => setOpen(false)}
              className="btn-dim btn btn-secondary"
            >
              Cancel
            </Button>
          </li>
        </ul>
      </div>
    </Modal>
  );
};

export const StatusChangeReason = ({ open, setOpen, onSubmit }: any) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        const ETA = values.ETA;
        onSubmit(reason, dayjs(ETA)?.format("YYYY-MM-DD")); // Call your reject function here
        form.resetFields();
        setOpen(false);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <Modal
      title={undefined}
      open={open}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" name="ownerChange">
        <Form.Item
          name="reason"
          label="Reason for Holding"
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." rows={4} />
        </Form.Item>
        <Form.Item
          name="ETA"
          label="Estimated Time of Arrival (ETA)"
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <DatePicker
            id="expected-resume-date"
            style={{ width: "100%" }}
            placeholder="Select expected resume date"
            format="YYYY-MM-DD"
            disabledDate={(current) => {
              // Disable past dates
              return current && current < dayjs().startOf("day");
            }}
          />
        </Form.Item>
      </Form>
      <div className="actions clearfix">
        <ul className="pt-2">
          <li>
            <Button color="primary" type="submit" onClick={handleOk}>
              Submit
            </Button>
          </li>
          <li>
            <Button
              color="primary"
              onClick={() => setOpen(false)}
              className="btn-dim btn btn-secondary"
            >
              Cancel
            </Button>
          </li>
        </ul>
      </div>
    </Modal>
  );
};

export default PhaseFieldDetailsUpdate;
